import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { customerService } from '../services/customerService';
import { formatCurrency } from './CurrencyTextField';
import { formatDateOnly } from '../utils/dateUtils';

const CustomerCreditLedger = ({ customerId }) => {
  const navigate = useNavigate();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await customerService.getCreditLedger(customerId);
        if (!cancelled) setLedger(res?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load payment ledger');
          setLedger(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const formatDate = (d) => formatDateOnly(d, 'MM/dd/yyyy') || '—';
  const invoiceLabel = (a) =>
    a.invoice_identifier?.trim() || a.invoice_number?.trim() || `#${a.customer_invoice_id}`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!ledger) return null;

  const sourcePayments = (ledger.payments || []).filter((p) => !p.is_synthetic);

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f8f9fa' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Credit on account (unapplied payments)
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {formatCurrency(ledger.credit_available, '$0.00')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Sum of unapplied balances on customer payments. Applying credit allocates from original payment(s) in date order.
        </Typography>
      </Paper>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Payments received
      </Typography>
      {sourcePayments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No customer payments recorded.
        </Typography>
      ) : (
        <TableContainer sx={{ mb: 2, border: 1, borderColor: 'borderSubtle.main' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'surfaceMuted.main' }}>
                <TableCell sx={{ fontWeight: 600 }}>Payment #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Applied</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Unapplied</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sourcePayments.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                      onClick={() => navigate(`/customer-payments/${p.id}`)}
                    >
                      {p.payment_number || `#${p.id}`}
                    </Button>
                  </TableCell>
                  <TableCell>{formatDate(p.payment_date)}</TableCell>
                  <TableCell>{p.payment_method || '—'}</TableCell>
                  <TableCell>{p.reference_number || '—'}</TableCell>
                  <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(p.amount_allocated)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: p.amount_unapplied > 0 ? 600 : 400 }}>
                    {formatCurrency(p.amount_unapplied)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Applications to invoices
      </Typography>
      {(ledger.allocations || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No payment applications yet.
        </Typography>
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'borderSubtle.main' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'surfaceMuted.main' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Source payment</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(ledger.allocations || []).map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{formatDate(a.created_at || a.payment_date)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                      onClick={() => navigate(`/customer-payments/${a.payment_id}`)}
                    >
                      {a.payment_number || `#${a.payment_id}`}
                    </Button>
                    {a.payment_method === 'Customer Credit' && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        (legacy)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                      onClick={() => navigate(`/customer-invoices/${a.customer_invoice_id}`)}
                    >
                      {invoiceLabel(a)}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {a.work_order_id ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={() => navigate(`/work-orders/${a.work_order_id}`)}
                      >
                        {a.work_order_identifier || `#${a.work_order_id}`}
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(a.amount_applied)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CustomerCreditLedger;
